import type {
    ProteinAtomSite,
    ProteinBond,
    ProteinChain,
    ProteinSecondaryStructureRange,
    ProteinStructure,
} from '@/adapters/biology/_contract/proteinStructure';

const ALTERNATE_LOCATION_PRIORITY = new Map<string, number>([
    ['', 0],
    ['A', 1],
]);

function compareAlternateLocations(left: ProteinAtomSite, right: ProteinAtomSite): number {
    const occupancyDifference = right.occupancy - left.occupancy;
    if (occupancyDifference !== 0) return occupancyDifference;

    const leftPriority = ALTERNATE_LOCATION_PRIORITY.get(left.alternateLocation ?? '') ?? 2;
    const rightPriority = ALTERNATE_LOCATION_PRIORITY.get(right.alternateLocation ?? '') ?? 2;
    return leftPriority - rightPriority;
}

function coordinateTransform(chains: ProteinChain[]) {
    const points = chains.flatMap((chain) => chain.residues.map((residue) => residue.position));
    const minimum = [Infinity, Infinity, Infinity];
    const maximum = [-Infinity, -Infinity, -Infinity];
    for (const point of points) {
        for (let axis = 0; axis < 3; axis += 1) {
            minimum[axis] = Math.min(minimum[axis], point[axis]);
            maximum[axis] = Math.max(maximum[axis], point[axis]);
        }
    }

    const center = minimum.map((value, axis) => (value + maximum[axis]) / 2);
    const extent = Math.max(...maximum.map((value, axis) => value - minimum[axis]));
    const scale = extent > 0 ? 8 / extent : 1;

    const transform = (position: [number, number, number]): [number, number, number] => [
        (position[0] - center[0]) * scale,
        (position[1] - center[1]) * scale,
        (position[2] - center[2]) * scale,
    ];
    return { scale, transform };
}

export function normalizeProteinStructure(
    id: string,
    atomSites: readonly ProteinAtomSite[],
    secondaryStructureRanges: readonly ProteinSecondaryStructureRange[] = [],
    bonds: readonly ProteinBond[] = [],
): ProteinStructure {
    const firstModel = atomSites.find((atomSite) => atomSite.modelNumber > 0)?.modelNumber;
    const modelAtoms = atomSites.filter((atomSite) =>
        atomSite.modelNumber === (firstModel ?? 1)
        && atomSite.recordKind === 'ATOM'
        && atomSite.alternateLocation !== '.'
        && atomSite.alternateLocation !== '?'
        && Number.isFinite(atomSite.residueSequence)
        && atomSite.position.every(Number.isFinite)
    );

    const atomByIdentity = new Map<string, ProteinAtomSite>();
    for (const atom of modelAtoms) {
        const identity = `${atom.chainId}\u0000${atom.residueSequence}\u0000${atom.atomName}`;
        const existing = atomByIdentity.get(identity);
        if (!existing || compareAlternateLocations(atom, existing) < 0) atomByIdentity.set(identity, atom);
    }
    const selectedAtoms = [...atomByIdentity.values()];
    const alphaCarbons = selectedAtoms.filter((atom) => atom.atomName === 'CA');
    const residueByIdentity = new Map<string, ProteinAtomSite>();
    for (const atomSite of alphaCarbons) {
        const identity = `${atomSite.chainId}\u0000${atomSite.residueSequence}`;
        const existing = residueByIdentity.get(identity);
        if (existing === undefined || compareAlternateLocations(atomSite, existing) < 0) {
            residueByIdentity.set(identity, atomSite);
        }
    }

    const chainsById = new Map<string, ProteinChain>();
    for (const atomSite of residueByIdentity.values()) {
        const chain = chainsById.get(atomSite.chainId) ?? { id: atomSite.chainId, residues: [] };
        const secondaryStructure = secondaryStructureRanges.find((range) =>
            range.chainId === atomSite.chainId
            && atomSite.residueSequence >= range.startSequence
            && atomSite.residueSequence <= range.endSequence
        )?.type ?? 'loop';
        chain.residues.push({
            sequence: atomSite.residueSequence,
            name: atomSite.residueName,
            secondaryStructure,
            atomIds: selectedAtoms.filter((atom) =>
                atom.chainId === atomSite.chainId && atom.residueSequence === atomSite.residueSequence
            ).map((atom) => atom.id),
            position: atomSite.position,
        });
        chainsById.set(atomSite.chainId, chain);
    }

    const chains = [...chainsById.values()]
        .map((chain) => ({
            ...chain,
            residues: chain.residues.sort((left, right) => left.sequence - right.sequence),
        }))
        .sort((left, right) => left.id.localeCompare(right.id));

    if (chains.length === 0) {
        throw new Error(`The mmCIF structure "${id}" contains no usable alpha-carbon atoms.`);
    }

    const { scale, transform } = coordinateTransform(chains);
    const atoms = selectedAtoms.map((atom) => ({
        id: atom.id,
        name: atom.atomName,
        element: atom.element,
        chainId: atom.chainId,
        residueSequence: atom.residueSequence,
        position: transform(atom.position),
    }));
    const selectedIds = new Set(atoms.map((atom) => atom.id));
    return {
        id,
        chains: chains.map((chain) => ({
            ...chain,
            residues: chain.residues.map((residue) => ({ ...residue, position: transform(residue.position) })),
        })),
        atoms,
        bonds: bonds.filter((bond) => selectedIds.has(bond.atomIdA) && selectedIds.has(bond.atomIdB)),
        angstromToWorldScale: scale,
    };
}
